import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import { FaPlus, FaTimes } from "react-icons/fa";
import { catalogApi } from "../api/services";
import { resolveAssetUrl } from "../utils/assets";
import "./FeaturedProductPicker.css";

const MAX_FEATURED = 12;

const FeaturedProductPicker = ({
  selectedIds = [],
  onChange,
  limit = 9,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedMap, setSelectedMap] = useState(() => new Map());
  const [searching, setSearching] = useState(false);
  const timerRef = useRef(null);

  // Resolve currently selected products for chips
  useEffect(() => {
    let cancelled = false;
    const ids = selectedIds.map(String).filter(Boolean);
    if (!ids.length) {
      setSelectedMap(new Map());
      return undefined;
    }

    catalogApi
      .adminProducts({ ids: ids.join(","), limit: ids.length })
      .then((res) => {
        if (cancelled) return;
        const next = new Map();
        (res.data.data.products || []).forEach((p) => next.set(String(p.id), p));
        setSelectedMap(next);
      })
      .catch(() => {
        if (!cancelled) setSelectedMap(new Map());
      });

    return () => {
      cancelled = true;
    };
  }, [selectedIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced typeahead search
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await catalogApi.adminProducts({
          search: query.trim() || undefined,
          status: "active",
          limit: 20,
          page: 1,
          sort: "newest",
        });
        setResults(res.data.data.products || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 280);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  const selectedProducts = useMemo(
    () =>
      selectedIds
        .map((id) => selectedMap.get(String(id)))
        .filter(Boolean),
    [selectedIds, selectedMap]
  );

  const available = useMemo(() => {
    const chosen = new Set(selectedIds.map(String));
    return results.filter((p) => !chosen.has(String(p.id))).slice(0, 20);
  }, [results, selectedIds]);

  const addProduct = (product) => {
    if (selectedIds.length >= MAX_FEATURED) return;
    const id = String(product.id);
    onChange([...selectedIds, id]);
    setSelectedMap((prev) => new Map(prev).set(id, product));
    setQuery("");
  };

  const removeProduct = (id) => {
    onChange(selectedIds.filter((x) => String(x) !== String(id)));
  };

  const moveProduct = (index, direction) => {
    const next = [...selectedIds];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="featured-picker">
      <p className="featured-picker-help">
        Pick up to {limit} products for the home page. Leave empty to show the latest
        products automatically.
      </p>

      {selectedProducts.length > 0 && (
        <div className="featured-picker-selected">
          {selectedProducts.map((product, index) => {
            const image = resolveAssetUrl(product.images?.[0] || product.image);
            return (
              <div className="featured-picker-chip" key={product.id}>
                <div className="featured-picker-chip-thumb">
                  {image ? (
                    <img src={image} alt="" loading="lazy" decoding="async" />
                  ) : null}
                </div>
                <div className="featured-picker-chip-body">
                  <strong>{product.name}</strong>
                  <small>{product.sku || product.slug}</small>
                </div>
                <div className="featured-picker-chip-actions">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={index === 0}
                    onClick={() => moveProduct(index, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={index === selectedProducts.length - 1}
                    onClick={() => moveProduct(index, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeProduct(product.id)}
                  >
                    <FaTimes />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Form.Group className="mb-2">
        <Form.Label>Search products to add</Form.Label>
        <Form.Control
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, SKU, or slug..."
        />
      </Form.Group>

      <ListGroup className="featured-picker-results">
        {searching ? (
          <ListGroup.Item className="text-muted d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" /> Searching…
          </ListGroup.Item>
        ) : available.length === 0 ? (
          <ListGroup.Item className="text-muted">
            {query ? "No matching products" : "Type to search the catalog"}
          </ListGroup.Item>
        ) : (
          available.map((product) => (
            <ListGroup.Item
              key={product.id}
              className="featured-picker-result"
              action
              onClick={() => addProduct(product)}
            >
              <div>
                <strong>{product.name}</strong>
                <div className="text-muted small">{product.sku || product.slug}</div>
              </div>
              <Badge bg={product.isActive ? "success" : "secondary"}>
                {product.isActive ? "Active" : "Inactive"}
              </Badge>
              <Button size="sm" variant="outline-dark">
                <FaPlus />
              </Button>
            </ListGroup.Item>
          ))
        )}
      </ListGroup>
    </div>
  );
};

export default FeaturedProductPicker;
